import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Edit2, Trash2, Users, TrendingUp, TrendingDown, Percent, Clock, FileText, XCircle, DollarSign, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { getCashMovementTerms } from '@/lib/terminology-config';

type ConfidenceLevel = 'confirmed' | 'estimated' | 'tbc';

const CONFIDENCE_CONFIG: Record<ConfidenceLevel, { emoji: string; label: string; tooltip: string; bg: string; border: string; text: string }> = {
  confirmed: {
    emoji: '🟢',
    label: 'Confirmed',
    tooltip: 'This figure is confirmed — you know this number with certainty.',
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
  },
  estimated: {
    emoji: '🟡',
    label: 'Estimated',
    tooltip: 'Best guess — this figure needs further research or confirmation.',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
  },
  tbc: {
    emoji: '🔴',
    label: 'TBC',
    tooltip: 'Placeholder — this is a known unknown. The figure needs to be established.',
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
  },
};

const CONFIDENCE_CYCLE: ConfidenceLevel[] = ['confirmed', 'estimated', 'tbc'];

interface AdjustmentCardProps {
  adjustment: any;
  onEdit?: () => void;
  onRemove?: () => void;
  className?: string;
}

export function AdjustmentCard({ adjustment, onEdit, onRemove, className }: AdjustmentCardProps) {
  // Safety check
  if (!adjustment) {
    return null;
  }

  // Partial confidence state — cycles through confirmed → estimated → tbc on click
  const [confidence, setConfidence] = useState<ConfidenceLevel>('confirmed');
  const cycleConfidence = () => {
    const currentIndex = CONFIDENCE_CYCLE.indexOf(confidence);
    const nextIndex = (currentIndex + 1) % CONFIDENCE_CYCLE.length;
    setConfidence(CONFIDENCE_CYCLE[nextIndex]);
  };
  const conf = CONFIDENCE_CONFIG[confidence];

  const renderHeadcountAdjustment = () => {
    if (adjustment.type === 'hire') {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Hire {adjustment.numberOfEmployees} {adjustment.role}
                  {adjustment.numberOfEmployees > 1 ? 's' : ''}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.entity} • Starting {format(adjustment.startDate, 'MMM d, yyyy')}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Average Salary:</span>
              <span>£{adjustment.averageSalary.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Annual Cost:</span>
              <span className="font-medium">£{Math.round(adjustment.totalAnnualCost).toLocaleString()}</span>
            </div>
          </div>

          <div className="ml-11 p-2 bg-red-50 border border-red-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-red-700 font-medium">Monthly Impact:</span>
              <span className="text-red-700 font-semibold">
                -£{Math.round(adjustment.monthlyCost).toLocaleString()}/month
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === 'remove') {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Remove {adjustment.numberOfEmployees} {adjustment.role}
                  {adjustment.numberOfEmployees > 1 ? 's' : ''}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.entity} • Ending {format(adjustment.endDate, 'MMM d, yyyy')}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Annual Savings:</span>
              <span className="font-medium text-green-700">
                +£{Math.round(adjustment.totalAnnualSavings).toLocaleString()}
              </span>
            </div>
            {adjustment.totalSeverance > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Severance Cost:</span>
                <span className="text-red-600">-£{adjustment.totalSeverance.toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Notice Period:</span>
              <span>
                {adjustment.noticePeriod} month{adjustment.noticePeriod > 1 ? 's' : ''} (-£
                {Math.round(adjustment.noticePeriodCost).toLocaleString()})
              </span>
            </div>
          </div>

          <div className="ml-11 p-2 bg-green-50 border border-green-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-green-700 font-medium">Monthly Savings:</span>
              <span className="text-green-700 font-semibold">
                +£{Math.round(adjustment.monthlySavings).toLocaleString()}/month
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === 'adjust-salary') {
      const isIncrease = adjustment.adjustmentPercent > 0;
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className={cn('p-2 rounded-lg', isIncrease ? 'bg-red-100' : 'bg-green-100')}>
                {isIncrease ? (
                  <TrendingUp className="h-4 w-4 text-red-600" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-green-600" />
                )}
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  {isIncrease ? 'Increase' : 'Decrease'} Salaries by {Math.abs(adjustment.adjustmentPercent)}%
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.role || 'All Roles'} • {adjustment.entity || 'All Entities'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Effective {format(adjustment.effectiveDate, 'MMM d, yyyy')}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Affected Employees:</span>
              <span>~{adjustment.estimatedAffectedEmployees} people</span>
            </div>
            {adjustment.reason && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Reason:</span>
                <span>{adjustment.reason}</span>
              </div>
            )}
          </div>

          <div
            className={cn(
              'ml-11 p-2 border rounded text-xs',
              isIncrease ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
            )}
          >
            <div className="flex items-center justify-between">
              <span className={cn('font-medium', isIncrease ? 'text-red-700' : 'text-green-700')}>
                Monthly Impact:
              </span>
              <span className={cn('font-semibold', isIncrease ? 'text-red-700' : 'text-green-700')}>
                {isIncrease ? '-' : '+'}£{Math.abs(Math.round(adjustment.monthlyImpact)).toLocaleString()}/month
              </span>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderRevenueAdjustment = () => {
    if (adjustment.type === 'adjust-revenue-percent') {
      const isIncrease = adjustment.adjustmentPercent > 0;
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className={cn('p-2 rounded-lg', isIncrease ? 'bg-green-100' : 'bg-red-100')}>
                <Percent className={cn('h-4 w-4', isIncrease ? 'text-green-600' : 'text-red-600')} />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  {isIncrease ? 'Increase' : 'Decrease'} Revenue by {Math.abs(adjustment.adjustmentPercent)}%
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.entity || 'All Entities'} • Effective {format(adjustment.effectiveDate, 'MMM d, yyyy')}
                </p>
                {adjustment.reason && (
                  <p className="text-xs text-muted-foreground">Reason: {adjustment.reason}</p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current Revenue:</span>
              <span>£{adjustment.estimatedCurrentRevenue.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Annual Change:</span>
              <span className={cn('font-medium', isIncrease ? 'text-green-700' : 'text-red-700')}>
                {isIncrease ? '+' : ''}£{Math.round(adjustment.adjustmentAmount).toLocaleString()}
              </span>
            </div>
          </div>

          <div
            className={cn(
              'ml-11 p-2 border rounded text-xs',
              isIncrease ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
            )}
          >
            <div className="flex items-center justify-between">
              <span className={cn('font-medium', isIncrease ? 'text-green-700' : 'text-red-700')}>
                Monthly Impact:
              </span>
              <span className={cn('font-semibold', isIncrease ? 'text-green-700' : 'text-red-700')}>
                {isIncrease ? '+' : ''}£{Math.abs(Math.round(adjustment.monthlyImpact)).toLocaleString()}/month
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === 'delay-invoice') {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="h-4 w-4 text-orange-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">Delay Invoice {adjustment.invoiceId}</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.customer} • £{adjustment.amount.toLocaleString()}
                </p>
                {adjustment.reason && (
                  <p className="text-xs text-muted-foreground">Reason: {adjustment.reason}</p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Original Due:</span>
              <span>{format(new Date(adjustment.originalDueDate), 'MMM d, yyyy')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">New Due Date:</span>
              <span>{format(adjustment.newDueDate, 'MMM d, yyyy')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Delay:</span>
              <span className="text-orange-700 font-medium">{adjustment.delayDays} days</span>
            </div>
          </div>

          <div className="ml-11 p-2 bg-orange-50 border border-orange-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-orange-700 font-medium">Cash Flow Impact:</span>
              <span className="text-orange-700 font-semibold">
                £{adjustment.amount.toLocaleString()} delayed
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === 'add-contract') {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileText className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">New Contract: {adjustment.customer}</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.entity} • £{adjustment.contractValue.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">
                  {format(adjustment.startDate, 'MMM yyyy')} - {format(adjustment.endDate, 'MMM yyyy')}
                </p>
                {adjustment.description && (
                  <p className="text-xs text-muted-foreground">{adjustment.description}</p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Contract Value:</span>
              <span className="font-medium">£{adjustment.contractValue.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Duration:</span>
              <span>{adjustment.durationMonths} months</span>
            </div>
          </div>

          <div className="ml-11 p-2 bg-green-50 border border-green-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-green-700 font-medium">Monthly Revenue:</span>
              <span className="text-green-700 font-semibold">
                +£{Math.round(adjustment.monthlyRevenue).toLocaleString()}/month
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === 'cancel-project') {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-4 w-4 text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">Cancel: {adjustment.projectName}</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.customer} • Cancelled {format(adjustment.cancellationDate, 'MMM d, yyyy')}
                </p>
                {adjustment.reason && (
                  <p className="text-xs text-muted-foreground">Reason: {adjustment.reason}</p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Project Value:</span>
              <span>£{adjustment.projectValue.toLocaleString()}</span>
            </div>
            {adjustment.refundAmount > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Refund ({adjustment.refundPercent}%):</span>
                <span className="text-red-600">-£{Math.round(adjustment.refundAmount).toLocaleString()}</span>
              </div>
            )}
          </div>

          <div className="ml-11 p-2 bg-red-50 border border-red-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-red-700 font-medium">Net Revenue Loss:</span>
              <span className="text-red-700 font-semibold">
                -£{Math.round(adjustment.netRevenueLoss).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderCostAdjustment = () => {
    if (adjustment.type === 'reduce-costs') {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingDown className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Reduce {adjustment.category} by {Math.abs(adjustment.reductionPercent)}%
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.entity} • Effective {format(adjustment.effectiveDate, 'MMM d, yyyy')}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current Monthly Cost:</span>
              <span>£{adjustment.estimatedCurrentCost.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Annual Savings:</span>
              <span className="font-medium text-green-700">
                +£{Math.round(adjustment.annualSavings).toLocaleString()}
              </span>
            </div>
          </div>

          <div className="ml-11 p-2 bg-green-50 border border-green-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-green-700 font-medium">Monthly Savings:</span>
              <span className="text-green-700 font-semibold">
                +£{Math.round(adjustment.monthlySavings).toLocaleString()}/month
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === 'increase-costs') {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <TrendingUp className="h-4 w-4 text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Increase {adjustment.category} by {adjustment.increasePercent}%
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.entity} • Effective {format(adjustment.effectiveDate, 'MMM d, yyyy')}
                </p>
                {adjustment.reason && (
                  <p className="text-xs text-muted-foreground">Reason: {adjustment.reason}</p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current Monthly Cost:</span>
              <span>£{adjustment.estimatedCurrentCost.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Annual Increase:</span>
              <span className="font-medium text-red-700">
                -£{Math.round(adjustment.annualCostIncrease).toLocaleString()}
              </span>
            </div>
          </div>

          <div className="ml-11 p-2 bg-red-50 border border-red-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-red-700 font-medium">Monthly Cost Increase:</span>
              <span className="text-red-700 font-semibold">
                -£{Math.round(adjustment.monthlyCostIncrease).toLocaleString()}/month
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === 'delay-payment') {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">Delay Payment: {adjustment.supplier}</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  £{adjustment.amount.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Original Due:</span>
              <span>{format(new Date(adjustment.originalDueDate), 'MMM d, yyyy')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">New Due Date:</span>
              <span>{format(adjustment.newDueDate, 'MMM d, yyyy')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Delay:</span>
              <span className="text-blue-700 font-medium">{adjustment.delayDays} days</span>
            </div>
          </div>

          <div className="ml-11 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-blue-700 font-medium">Cash Preserved:</span>
              <span className="text-blue-700 font-semibold">
                £{adjustment.amount.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === 'one-time-expense') {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Plus className="h-4 w-4 text-orange-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">{adjustment.description}</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.category} • {adjustment.entity}
                </p>
                <p className="text-xs text-muted-foreground">
                  {format(adjustment.date, 'MMM d, yyyy')}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 p-2 bg-orange-50 border border-orange-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-orange-700 font-medium">One-Time Expense:</span>
              <span className="text-orange-700 font-semibold">
                -£{adjustment.amount.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderProjectAdjustment = () => {
    if (adjustment.type === 'add_project') {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Plus className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">{adjustment.projectName}</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.entity} • £{adjustment.totalValue.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">
                  {format(adjustment.startDate, 'MMM yyyy')} - {format(adjustment.endDate, 'MMM yyyy')} • {adjustment.billingSchedule}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Duration:</span>
              <span>{adjustment.projectDuration} months</span>
            </div>
            {adjustment.billingSchedule !== 'completion' && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Monthly Revenue:</span>
                <span className="font-medium text-blue-700">
                  +£{Math.round(adjustment.monthlyRevenue).toLocaleString()}/month
                </span>
              </div>
            )}
          </div>

          <div className="ml-11 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-blue-700 font-medium">Total Contract Value:</span>
              <span className="text-blue-700 font-semibold">
                +£{adjustment.totalValue.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === 'delay_project') {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="h-4 w-4 text-orange-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">Delay: {adjustment.projectName}</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  +{adjustment.delayWeeks} weeks delay
                </p>
                {adjustment.reason && (
                  <p className="text-xs text-muted-foreground">Reason: {adjustment.reason}</p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Original Start:</span>
              <span>{format(adjustment.originalStartDate, 'MMM d, yyyy')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">New Start:</span>
              <span className="text-orange-700 font-medium">{format(adjustment.newStartDate, 'MMM d, yyyy')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">New End:</span>
              <span>{format(adjustment.newEndDate, 'MMM d, yyyy')}</span>
            </div>
          </div>

          <div className="ml-11 p-2 bg-orange-50 border border-orange-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-orange-700 font-medium">Revenue Timeline Shift:</span>
              <span className="text-orange-700 font-semibold">
                +{adjustment.delayWeeks} weeks
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === 'change_project_value') {
      const isIncrease = adjustment.revenueDelta >= 0;
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className={cn('p-2 rounded-lg', isIncrease ? 'bg-green-100' : 'bg-red-100')}>
                <TrendingUp className={cn('h-4 w-4', isIncrease ? 'text-green-600' : 'text-red-600')} />
              </div>
              <div>
                <h4 className="font-semibold text-sm">{adjustment.projectName}</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Value change: {isIncrease ? '+' : ''}£{Math.abs(adjustment.revenueDelta).toLocaleString()} ({adjustment.percentChange.toFixed(1)}%)
                </p>
                {adjustment.reason && (
                  <p className="text-xs text-muted-foreground">Reason: {adjustment.reason}</p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Original Value:</span>
              <span>£{adjustment.originalValue.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">New Value:</span>
              <span className="font-medium">£{adjustment.newValue.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Effective:</span>
              <span>{format(adjustment.effectiveDate, 'MMM d, yyyy')}</span>
            </div>
          </div>

          <div className={cn(
            'ml-11 p-2 border rounded text-xs',
            isIncrease ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          )}>
            <div className="flex items-center justify-between">
              <span className={cn('font-medium', isIncrease ? 'text-green-700' : 'text-red-700')}>
                Revenue Delta:
              </span>
              <span className={cn('font-semibold', isIncrease ? 'text-green-700' : 'text-red-700')}>
                {isIncrease ? '+' : ''}£{Math.abs(adjustment.revenueDelta).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === 'cancel_project') {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-4 w-4 text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">Cancel: {adjustment.projectName}</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Cancelled {format(adjustment.cancellationDate, 'MMM d, yyyy')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {adjustment.workCompletedPercent.toFixed(0)}% complete
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Original Value:</span>
              <span>£{adjustment.originalValue.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Earned Revenue:</span>
              <span className="text-green-700">+£{Math.round(adjustment.earnedRevenue).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Lost Revenue:</span>
              <span className="text-red-700">-£{Math.round(adjustment.lostRevenue).toLocaleString()}</span>
            </div>
            {adjustment.refundPenalty > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Refund/Penalty:</span>
                <span className="text-red-700">-£{Math.round(adjustment.refundPenalty).toLocaleString()}</span>
              </div>
            )}
          </div>

          <div className="ml-11 p-2 bg-red-50 border border-red-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-red-700 font-medium">Total Lost Revenue:</span>
              <span className="text-red-700 font-semibold">
                -£{Math.round(adjustment.netLostRevenue).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderFinancingAdjustment = () => {
    if (adjustment.type === 'add-loan') {
      const loanTypeLabel = adjustment.loanType === 'loan' ? 'Term Loan' : 
                           adjustment.loanType === 'overdraft' ? 'Overdraft' : 'Line of Credit';
      const scheduleLabel = adjustment.repaymentSchedule === 'monthly' ? 'Monthly' :
                           adjustment.repaymentSchedule === 'quarterly' ? 'Quarterly' : 'Bullet';
      
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Plus className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Add {loanTypeLabel} - £{adjustment.amount.toLocaleString()}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.interestRate}% • {adjustment.termMonths} months • {scheduleLabel} repayment
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Start Date:</span>
              <span>{format(adjustment.startDate, 'MMM d, yyyy')}</span>
            </div>
            {adjustment.repaymentSchedule !== 'bullet' && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Monthly Payment:</span>
                <span className="text-red-600 font-medium">
                  £{adjustment.monthlyPayment.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Interest:</span>
              <span className="text-orange-600 font-medium">
                £{adjustment.totalInterest.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Repayment:</span>
              <span className="font-medium">
                £{adjustment.totalRepayment.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === 'repay-debt') {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingDown className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Repay Debt Early - {adjustment.debtName}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  £{adjustment.repaymentAmount.toLocaleString()} on {format(adjustment.repaymentDate, 'MMM d, yyyy')}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Interest Savings:</span>
              <span className="text-green-600 font-medium">
                £{adjustment.interestSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Remaining Balance:</span>
              <span>£{adjustment.remainingBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === 'add-investment') {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Investment {getCashMovementTerms().in} - {adjustment.source}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.incomeType === 'one-time' ? 'One-time payment' : `Recurring ${adjustment.frequency}`} • 
                  {format(adjustment.date, 'MMM d, yyyy')}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {adjustment.incomeType === 'one-time' ? 'Amount:' : 'Per Payment:'}
              </span>
              <span className="text-green-600 font-medium">
                £{adjustment.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {adjustment.incomeType === 'one-time' ? `Total ${getCashMovementTerms().in}:` : `Annual ${getCashMovementTerms().in}:`}
              </span>
              <span className="text-green-600 font-medium">
                £{adjustment.annualIncome.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  // Generic fallback renderer for any adjustment type not specifically handled
  const renderGenericAdjustment = () => {
    const amount = adjustment.amount || 0;
    const isPositive = amount > 0;

    // win-customer — revenue inflow with date
    if (adjustment.type === 'win-customer') {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">{adjustment.name}</h4>
                {adjustment.description && (
                  <p className="text-xs text-muted-foreground mt-1">{adjustment.description}</p>
                )}
                {adjustment.details?.customer && (
                  <p className="text-xs text-muted-foreground">Customer: {adjustment.details.customer}</p>
                )}
                {adjustment.details?.paymentTerms && (
                  <p className="text-xs text-muted-foreground">Payment terms: {adjustment.details.paymentTerms} days</p>
                )}
                {adjustment.effectiveDate && (
                  <p className="text-xs text-muted-foreground">
                    Expected: {format(new Date(adjustment.effectiveDate), 'MMM d, yyyy')}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && <Button variant="ghost" size="sm" onClick={onEdit}><Edit2 className="h-3 w-3" /></Button>}
              {onRemove && <Button variant="ghost" size="sm" onClick={onRemove}><Trash2 className="h-3 w-3" /></Button>}
            </div>
          </div>
          <div className="ml-11 p-2 bg-green-50 border border-green-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-green-700 font-medium">Revenue Inflow:</span>
              <span className="text-green-700 font-semibold">+£{Math.abs(amount).toLocaleString()}</span>
            </div>
          </div>
        </div>
      );
    }

    // hire-employees — recurring payroll cost
    if (adjustment.type === 'hire-employees') {
      const monthlyCost = adjustment.details?.monthlyCost ?? Math.abs(amount);
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">{adjustment.name}</h4>
                {adjustment.description && (
                  <p className="text-xs text-muted-foreground mt-1">{adjustment.description}</p>
                )}
                {adjustment.details?.department && (
                  <p className="text-xs text-muted-foreground">Department: {adjustment.details.department}</p>
                )}
                {adjustment.details?.period && (
                  <p className="text-xs text-muted-foreground">Period: {adjustment.details.period}</p>
                )}
                {adjustment.effectiveDate && (
                  <p className="text-xs text-muted-foreground">
                    From: {format(new Date(adjustment.effectiveDate), 'MMM d, yyyy')}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && <Button variant="ghost" size="sm" onClick={onEdit}><Edit2 className="h-3 w-3" /></Button>}
              {onRemove && <Button variant="ghost" size="sm" onClick={onRemove}><Trash2 className="h-3 w-3" /></Button>}
            </div>
          </div>
          <div className="ml-11 p-2 bg-red-50 border border-red-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-red-700 font-medium">Monthly Payroll Cost:</span>
              <span className="text-red-700 font-semibold">-£{Math.abs(monthlyCost).toLocaleString()}/month</span>
            </div>
          </div>
        </div>
      );
    }

    // capital-purchase — one-off capex outflow
    if (adjustment.type === 'capital-purchase') {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Plus className="h-4 w-4 text-orange-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">{adjustment.name}</h4>
                {adjustment.description && (
                  <p className="text-xs text-muted-foreground mt-1">{adjustment.description}</p>
                )}
                {adjustment.effectiveDate && (
                  <p className="text-xs text-muted-foreground">
                    Date: {format(new Date(adjustment.effectiveDate), 'MMM d, yyyy')}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && <Button variant="ghost" size="sm" onClick={onEdit}><Edit2 className="h-3 w-3" /></Button>}
              {onRemove && <Button variant="ghost" size="sm" onClick={onRemove}><Trash2 className="h-3 w-3" /></Button>}
            </div>
          </div>
          <div className="ml-11 p-2 bg-orange-50 border border-orange-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-orange-700 font-medium">Capital Expenditure:</span>
              <span className="text-orange-700 font-semibold">-£{Math.abs(amount).toLocaleString()}</span>
            </div>
          </div>
        </div>
      );
    }

    const isRevenue = adjustment.type?.includes('revenue') || adjustment.type === 'add-revenue';
    const isCost = adjustment.type?.includes('cost') || adjustment.type?.includes('payment');
    
    return (
      <div className="space-y-2">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={cn('p-2 rounded-lg', 
              isRevenue ? 'bg-green-100' : isCost ? 'bg-blue-100' : 'bg-gray-100'
            )}>
              <FileText className={cn('h-4 w-4', 
                isRevenue ? 'text-green-600' : isCost ? 'text-blue-600' : 'text-gray-600'
              )} />
            </div>
            <div>
              <h4 className="font-semibold text-sm">{adjustment.name || 'Adjustment'}</h4>
              {adjustment.description && (
                <p className="text-xs text-muted-foreground mt-1">{adjustment.description}</p>
              )}
              {adjustment.date && typeof adjustment.date === 'string' && (
                <p className="text-xs text-muted-foreground">
                  {adjustment.date}
                </p>
              )}
              {adjustment.details?.period && (
                <p className="text-xs text-muted-foreground">Period: {adjustment.details.period}</p>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            {onEdit && (
              <Button variant="ghost" size="sm" onClick={onEdit}>
                <Edit2 className="h-3 w-3" />
              </Button>
            )}
            {onRemove && (
              <Button variant="ghost" size="sm" onClick={onRemove}>
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>

        {amount !== 0 && (
          <div className={cn(
            'ml-11 p-2 border rounded text-xs',
            isPositive ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          )}>
            <div className="flex items-center justify-between">
              <span className={cn('font-medium', isPositive ? 'text-green-700' : 'text-red-700')}>
                {isRevenue ? 'Revenue Impact:' : isCost ? 'Cost Impact:' : 'Impact:'}
              </span>
              <span className={cn('font-semibold', isPositive ? 'text-green-700' : 'text-red-700')}>
                {isPositive ? '+' : ''}£{Math.abs(amount).toLocaleString()}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Try specific renderers first, fall back to generic
  try {
    const specificContent = renderHeadcountAdjustment() || 
                            renderRevenueAdjustment() || 
                            renderCostAdjustment() || 
                            renderProjectAdjustment() || 
                            renderFinancingAdjustment();

    return (
      <div className={cn('p-4 border border-border rounded-lg bg-white hover:shadow-sm transition-shadow', className)}>
        {/* Confidence indicator — click to cycle through Confirmed → Estimated → TBC */}
        <div className="flex justify-end mb-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={cycleConfidence}
                  className={cn(
                    'flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium transition-all hover:opacity-80',
                    conf.bg, conf.border, conf.text
                  )}
                >
                  <span>{conf.emoji}</span>
                  <span>{conf.label}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[200px] text-center">
                <p>{conf.tooltip}</p>
                <p className="text-xs text-muted-foreground mt-1">Click to change confidence level</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {specificContent || renderGenericAdjustment()}
      </div>
    );
  } catch (error) {
    console.error('Error rendering adjustment card:', error, adjustment);
    return (
      <div className="p-4 border rounded-lg bg-red-50 border-red-200">
        <p className="text-sm text-red-700">Error rendering adjustment: {adjustment?.name || 'Unknown'}</p>
      </div>
    );
  }
}
